import { instance } from "api/apiBase";

export type MasterSearchEntityType =
  | "lead"
  | "contact"
  | "screen_applicant"
  | "report_renter"
  | "property"
  | "listing"
  | "lockbox";

export interface MasterSearchItem {
  type: MasterSearchEntityType;
  id: number;
  title: string;
  subtitle: string | null;
  context: Record<string, string | number | boolean | null>;
}

export interface MasterSearchGroup {
  type: MasterSearchEntityType;
  total: number;
  items: MasterSearchItem[];
}

interface MasterSearchResponse {
  query: string;
  groups: MasterSearchGroup[];
}

interface GetMasterSearchParams {
  q: string;
  limit?: number;
  types?: MasterSearchEntityType[];
}

const ENTITY_TYPES: MasterSearchEntityType[] = [
  "lead",
  "contact",
  "screen_applicant",
  "report_renter",
  "property",
  "listing",
  "lockbox"
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isEntityType = (value: unknown): value is MasterSearchEntityType =>
  typeof value === "string" &&
  ENTITY_TYPES.some((entityType) => entityType === value);

const parseContext = (value: unknown): MasterSearchItem["context"] => {
  if (!isRecord(value)) return {};

  return Object.entries(value).reduce<MasterSearchItem["context"]>(
    (context, [key, raw]) => {
      if (
        raw === null ||
        typeof raw === "string" ||
        typeof raw === "number" ||
        typeof raw === "boolean"
      )
        context[key] = raw;
      return context;
    },
    {}
  );
};

const parseItem = (value: unknown): MasterSearchItem | null => {
  if (!isRecord(value)) return null;
  if (!isEntityType(value.type)) return null;
  if (typeof value.id !== "number") return null;

  return {
    type: value.type,
    id: value.id,
    // A record matched on address alone can have no name/email/phone, so the
    // server sends a null title. Label it rather than dropping the row (which
    // would silently disagree with the group's total).
    title: typeof value.title === "string" ? value.title : `#${value.id}`,
    subtitle: typeof value.subtitle === "string" ? value.subtitle : null,
    context: parseContext(value.context)
  };
};

const parseGroup = (value: unknown): MasterSearchGroup | null => {
  if (!isRecord(value)) return null;
  if (!isEntityType(value.type)) return null;

  const items = Array.isArray(value.items)
    ? value.items
        .map(parseItem)
        .filter((item): item is MasterSearchItem => Boolean(item))
    : [];

  return {
    type: value.type,
    total: typeof value.total === "number" ? value.total : items.length,
    items
  };
};

const parseMasterSearchResponse = (body: unknown): MasterSearchResponse => {
  if (!isRecord(body)) return { query: "", groups: [] };

  const groups = Array.isArray(body.groups)
    ? body.groups
        .map(parseGroup)
        .filter((group): group is MasterSearchGroup => Boolean(group))
    : [];

  return {
    query: typeof body.query === "string" ? body.query : "",
    groups
  };
};

export const getMasterSearch = async (
  params: GetMasterSearchParams,
  signal?: AbortSignal
): Promise<MasterSearchResponse> => {
  return instance
    .get("/portal/v1/search", { params, signal })
    .then((res) => parseMasterSearchResponse(res.data));
};
